import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/lib/api";

const initial = {
  name: "",
  email: "",
  phone: "",
  attending: "yes",
  events: ["wedding", "reception"],
  guests: 1,
  dietary: "",
  message: "",
};

export default function RSVP({ open, setOpen }) {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleEvent = (e) =>
    setForm((f) => ({
      ...f,
      events: f.events.includes(e)
        ? f.events.filter((x) => x !== e)
        : [...f.events, e],
    }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Please share your name and email.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/rsvp`, {
        ...form,
        guests: Number(form.guests) || 1,
        events: form.attending === "yes" ? form.events : [],
      });
      setDone(true);
      toast.success("Your RSVP has been recorded — thank you.");
    } catch (err) {
      console.error(err);
      toast.error(
        err?.response?.data?.detail?.[0]?.msg ||
          "Something went wrong. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const close = () => {
    setOpen(false);
    setTimeout(() => {
      setDone(false);
      setForm(initial);
    }, 400);
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="fixed inset-0 z-[60] bg-black/55 backdrop-blur-sm flex items-end sm:items-center justify-center"
          data-testid="rsvp-modal"
          onClick={close}
        >
          <motion.div
            initial={{ y: 60, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 40, opacity: 0 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="bg-[#FBF5EA] w-full sm:max-w-2xl max-h-[92vh] overflow-y-auto relative"
          >
            <button
              onClick={close}
              data-testid="rsvp-close-button"
              className="absolute top-6 right-6 p-2 hover:rotate-90 transition-transform duration-300"
              aria-label="Close"
            >
              <X size={20} />
            </button>

            <div className="px-8 sm:px-14 py-12 sm:py-16">
              {!done ? (
                <>
                  <p className="overline">
                    <span className="divider-line mr-4" />
                    Kindly Respond
                  </p>
                  <h2 className="font-serif-wb text-4xl sm:text-5xl mt-5 tracking-tight">
                    We'd love to <span className="italic" style={{ color: "var(--wb-primary)" }}>celebrate</span>
                    <br /> with you.
                  </h2>
                  <p className="mt-4 text-[#5C5C5C] text-sm leading-relaxed">
                    Please respond by 15th June 2026.
                  </p>

                  <form onSubmit={submit} className="mt-10 space-y-6" data-testid="rsvp-form">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                      <div>
                        <label className="overline block mb-2">Full Name</label>
                        <input
                          className="wb-input"
                          value={form.name}
                          onChange={(e) => update("name", e.target.value)}
                          data-testid="rsvp-input-name"
                          placeholder="Your name"
                        />
                      </div>
                      <div>
                        <label className="overline block mb-2">Email</label>
                        <input
                          type="email"
                          className="wb-input"
                          value={form.email}
                          onChange={(e) => update("email", e.target.value)}
                          data-testid="rsvp-input-email"
                          placeholder="you@email.com"
                        />
                      </div>
                      <div>
                        <label className="overline block mb-2">Phone (optional)</label>
                        <input
                          className="wb-input"
                          value={form.phone}
                          onChange={(e) => update("phone", e.target.value)}
                          data-testid="rsvp-input-phone"
                          placeholder="+91 …"
                        />
                      </div>
                      <div>
                        <label className="overline block mb-2">Number of guests</label>
                        <input
                          type="number"
                          min="0"
                          max="20"
                          className="wb-input"
                          value={form.guests}
                          onChange={(e) => update("guests", e.target.value)}
                          data-testid="rsvp-input-guests"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="overline block mb-3">Will you attend?</label>
                      <div className="flex gap-3">
                        {[
                          { v: "yes", l: "Joyfully accept" },
                          { v: "no", l: "Regretfully decline" },
                        ].map((o) => (
                          <button
                            type="button"
                            key={o.v}
                            data-testid={`rsvp-attending-${o.v}`}
                            onClick={() => update("attending", o.v)}
                            className={`px-5 py-3 text-[0.72rem] uppercase tracking-[0.2em] border transition-all ${
                              form.attending === o.v
                                ? "bg-[#7A1F2B] text-white border-[#7A1F2B]"
                                : "bg-transparent text-[#1A1A1A] border-[#EFE6D2] hover:border-[#7A1F2B]"
                            }`}
                          >
                            {o.l}
                          </button>
                        ))}
                      </div>
                    </div>

                    {form.attending === "yes" && (
                      <>
                        <div>
                          <label className="overline block mb-3">Events you'll attend</label>
                          <div className="space-y-1">
                            <label className="wb-check" data-testid="rsvp-event-wedding">
                              <input
                                type="checkbox"
                                checked={form.events.includes("wedding")}
                                onChange={() => toggleEvent("wedding")}
                              />
                              <span>Wedding · Bhandara · 24 June 2026</span>
                            </label>
                            <label className="wb-check" data-testid="rsvp-event-reception">
                              <input
                                type="checkbox"
                                checked={form.events.includes("reception")}
                                onChange={() => toggleEvent("reception")}
                              />
                              <span>Reception · Hyderabad · 27 June 2026</span>
                            </label>
                          </div>
                        </div>

                        <div>
                          <label className="overline block mb-2">Dietary preferences</label>
                          <input
                            className="wb-input"
                            value={form.dietary}
                            onChange={(e) => update("dietary", e.target.value)}
                            data-testid="rsvp-input-dietary"
                            placeholder="Vegetarian, Jain, allergies…"
                          />
                        </div>
                      </>
                    )}

                    <div>
                      <label className="overline block mb-2">A note for the couple</label>
                      <textarea
                        className="wb-input"
                        rows={3}
                        value={form.message}
                        onChange={(e) => update("message", e.target.value)}
                        data-testid="rsvp-input-message"
                        placeholder="Optional — leave a blessing or note"
                      />
                    </div>

                    <div className="pt-4 flex items-center justify-between">
                      <p className="text-xs text-[#5C5C5C]">
                        With love from Varsha & Parag
                      </p>
                      <button
                        type="submit"
                        disabled={submitting}
                        data-testid="rsvp-submit-button"
                        className="wb-btn"
                      >
                        {submitting ? "Sending…" : "Send RSVP"}
                      </button>
                    </div>
                  </form>
                </>
              ) : (
                <div data-testid="rsvp-success" className="py-10 text-center">
                  <p className="overline">
                    <span className="divider-line mr-4" />
                    Thank you
                  </p>
                  <h3 className="font-serif-wb text-4xl sm:text-5xl mt-6 tracking-tight">
                    Your response is <span className="italic" style={{ color: "var(--wb-primary)" }}>safely with us.</span>
                  </h3>
                  <p className="mt-6 text-[#5C5C5C] max-w-md mx-auto leading-relaxed">
                    We can't wait to see you. Watch your inbox closer to the
                    date for travel notes and final timings.
                  </p>
                  <button
                    onClick={close}
                    data-testid="rsvp-close-success"
                    className="wb-btn mt-10"
                  >
                    Close
                  </button>
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
